import { writable, derived, get } from "svelte/store";

const DISMISS_KEY = "pwa-install-dismissed";
const DISMISS_DAYS = 14;

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
}

function createPwaStore() {
	const deferredPrompt = writable<BeforeInstallPromptEvent | null>(null);
	const dismissed = writable(isDismissed());
	const isStandalone = writable(false);

	const canInstall = derived(
		[deferredPrompt, dismissed, isStandalone],
		([$prompt, $dismissed, $standalone]) => !!$prompt && !$dismissed && !$standalone,
	);

	function init() {
		if (typeof window === "undefined") return;

		const mq = window.matchMedia("(display-mode: standalone)");
		isStandalone.set(mq.matches || (navigator as any).standalone === true);
		mq.addEventListener("change", (e) => isStandalone.set(e.matches));

		window.addEventListener("beforeinstallprompt", (e) => {
			e.preventDefault();
			deferredPrompt.set(e as BeforeInstallPromptEvent);
		});

		window.addEventListener("appinstalled", () => {
			deferredPrompt.set(null);
			isStandalone.set(true);
		});
	}

	async function install(): Promise<boolean> {
		const prompt = get(deferredPrompt);
		if (!prompt) return false;
		const { outcome } = await prompt.prompt();
		if (outcome === "accepted") {
			deferredPrompt.set(null);
			return true;
		}
		return false;
	}

	function dismiss() {
		localStorage.setItem(DISMISS_KEY, String(Date.now()));
		dismissed.set(true);
	}

	return { canInstall, isStandalone, init, install, dismiss };
}

function isDismissed(): boolean {
	if (typeof localStorage === "undefined") return false;
	const ts = localStorage.getItem(DISMISS_KEY);
	if (!ts) return false;
	const age = Date.now() - Number(ts);
	return age < DISMISS_DAYS * 86_400_000;
}

export const pwa = createPwaStore();
